import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, ElementsConsumer } from '@stripe/react-stripe-js';
import moment from 'moment';
import _ from 'lodash';

import { stripeApiKey } from 'config/stripe-api-key';
import StripeForm from 'components/shared/stripe-form';
import actions from 'api/actions';
import { ISODayToStringDay } from 'utils/date-utils';

const stripePromise = loadStripe(stripeApiKey);

function openIntercomMessage(message) {
  return () => {
    if (window && window.Intercom) {
      window.Intercom('showNewMessage', message);
    }
  };
}

class PlanPurchaseModal extends Component {
  static propTypes = {
    open: PropTypes.bool,
    dismiss: PropTypes.func,
    plan: PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      image: PropTypes.string,
      instruction_hours: PropTypes.number,
      price: PropTypes.number,
      description: PropTypes.string,
    }),
    parent: PropTypes.shape({
      id: PropTypes.number,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      email: PropTypes.string,
    }),
    learningCenterId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    learningCenter: PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
    }),
    selectedOptions: PropTypes.objectOf(PropTypes.string),
  };

  constructor(props) {
    super(props);

    this.state = {
      networkRequest: false,
      appliedDiscount: null,
      discountCodeFailure: false,
      discount_code: '',
    };

    this.onSubmit = this.onSubmit.bind(this);
    this.onDiscountCodeChange = this.onDiscountCodeChange.bind(this);
    this.onDiscountApply = this.onDiscountApply.bind(this);
  }

  onDiscountApply(code) {
    const { discount_code } = this.state;

    actions
      .rawPost(`discounts`, { discount_code, plan_id: this.props.plan.id })
      .then(r => this.setState({ appliedDiscount: r.data }))
      .catch(e => {
        this.setState({ discountCodeFailure: true });
        Sentry.captureException(e);
      });
  }

  onDiscountCodeChange(e) {
    this.setState({ discount_code: e.target.value });
  }

  onSubmit(params) {
    const { parent, learningCenter, learningCenterId, selectedOptions } =
      this.props;
    const { appliedDiscount } = this.state;
    if (parent && parent.id) {
      const planPurchaseParams = {
        ...params,
        parent_id: parent.id,
        learning_center_id: _.get(learningCenter, 'id', learningCenterId),
        selected_options: selectedOptions,
      };
      this.setState({ networkRequest: true }, () => {
        actions
          .rawPost(`parents/${parent.id}/plan_purchases`, {
            plan_purchase: planPurchaseParams,
          })
          .then(r => {
            this.props.PlanPurchaseStore.add(r.data);
            this.setState({ networkRequest: false }, this.props.onFinish);
          })
          .catch(e => {
            Sentry.captureException(e);
            this.setState({ networkRequest: false, error: true });
          });
      });
    } else {
      const planPurchaseParams = {
        ...params,
        learning_center_id: _.get(learningCenter, 'id', learningCenterId),
        selected_options: selectedOptions,
        discount_code: _.get(appliedDiscount, 'code'),
      };
      this.setState({ networkRequest: true }, () => {
        actions
          .post('parent-plan-purchase', { plan_purchase: planPurchaseParams })
          .then(() => {
            this.setState({ networkRequest: false }, this.props.onFinish);
          })
          .catch(e => {
            Sentry.captureException(e);
            this.setState({ networkRequest: false, error: true });
          });
      });
    }
  }

  render() {
    const { dismiss, open, plan, parent, learningCenter, selectedOptions } =
      this.props;
    const {
      networkRequest,
      appliedDiscount,
      discount_code,
      discountCodeFailure,
      error,
    } = this.state;

    return (
      <Modal size="lg" isOpen={open} toggle={dismiss}>
        <ModalHeader toggle={dismiss}>
          Purchase New Service: {plan.title}
        </ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-lg-6">
              <h5>{learningCenter.title}</h5>
              <ul className="list-unstyled">
                <li>
                  <strong>Service:</strong> {plan.title}
                </li>
                <li>
                  <strong>Instruction Hours:</strong> {plan.instruction_hours}
                </li>
                <li>
                  <strong>Price:</strong> ${plan.price}
                </li>
              </ul>
              {plan.options &&
                plan.options.map(option => {
                  let startDate, startTime, endDate, endTime;
                  const choice = _.find(option.choices, [
                    'id',
                    selectedOptions[option.id],
                  ]);
                  if (choice.schedule) {
                    startDate = moment.utc(choice.schedule.start_date);
                    startTime = moment.utc(
                      `${choice.schedule.start_date} ${choice.schedule.start_time}`,
                    );
                    endDate = moment.utc(choice.schedule.end_date);
                    endTime = moment.utc(
                      `${choice.schedule.start_date} ${choice.schedule.end_time}`,
                    );
                  }
                  return (
                    <ul key={option.id} className="list-unstyled">
                      <li>
                        <strong>{option.name}:</strong> {choice.name}
                      </li>
                      {choice.schedule && (
                        <React.Fragment>
                          <li>
                            <strong>Start:</strong>{' '}
                            {startDate.format('ddd MMM D')}
                          </li>
                          <li>
                            <strong>From:</strong> {startTime.format('h:mm a')}{' '}
                            to {endTime.format('h:mm a')}
                          </li>
                          <li>
                            <strong>Every:</strong>{' '}
                            {choice.schedule.days
                              .map(ISODayToStringDay)
                              .join(', ')}
                          </li>
                          <li>
                            <strong>Until:</strong>{' '}
                            {endDate.format('ddd MMM D')}
                          </li>
                        </React.Fragment>
                      )}
                    </ul>
                  );
                })}
            </div>
            <div className="col-lg-6">
              <h3>
                Due Now:&nbsp;
                {appliedDiscount ? (
                  <React.Fragment>
                    <s className="text-gray faded">${plan.price}</s>&nbsp;
                    <span className="text-green">
                      $
                      {plan.price -
                        plan.price * appliedDiscount.discount_percentage}
                    </span>
                  </React.Fragment>
                ) : (
                  <span>${plan.price}</span>
                )}
              </h3>
              {appliedDiscount ? (
                <p>Discount Applied: {appliedDiscount.code}</p>
              ) : (
                <div className="form-group flex-column d-flex ">
                  <label htmlFor="discount_code">Discount Code</label>
                  <input
                    type="text"
                    className="form-control"
                    name="discount_code"
                    placeholder="Enter Discount Code"
                    value={discount_code}
                    onChange={this.onDiscountCodeChange}
                  />
                  <button
                    type="button"
                    className="green mt-3 w-50 align-self-center"
                    onClick={this.onDiscountApply}
                  >
                    Apply Code
                  </button>
                  {discountCodeFailure && (
                    <p color="mt-3 text-red">Invalid Discount Code</p>
                  )}
                </div>
              )}
              {error ? (
                <div>
                  <p className="font-heavy text-red">
                    Something went wrong and we hit an error.
                  </p>
                  <p>
                    <button
                      type="button"
                      className="link blue"
                      onClick={openIntercomMessage(
                        'I received an error when trying to purchase a service',
                      )}
                    >
                      Please contact us
                    </button>{' '}
                    and we'll get it resolved.
                  </p>
                </div>
              ) : (
                <Elements stripe={stripePromise}>
                  <ElementsConsumer>
                    {({ stripe, elements }) => (
                      <StripeForm
                        stripe={stripe}
                        elements={elements}
                        modalDisabled={networkRequest}
                        onComplete={this.onSubmit}
                        planId={plan.id}
                        parent={parent}
                        disablePaymentElements={
                          appliedDiscount &&
                          plan.price -
                            plan.price * appliedDiscount.discount_percentage ===
                            0
                        }
                        appliedDiscount={appliedDiscount}
                      />
                    )}
                  </ElementsConsumer>
                </Elements>
              )}
            </div>
          </div>
        </ModalBody>
      </Modal>
    );
  }
}

export default PlanPurchaseModal;
